import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Button, Form, Input } from 'antd';
import axiosInstance from '../utils/axiosInstance';
import { removeAuthToken, setAuthToken } from '../utils/storageUtil';
import { useTranslation } from 'react-i18next';
import CustomSelect from '../Components/CustomSelect';
import AppShell from '../Components/AppShell';

const Language_option = {
    "en": "English",
    "zh": "中文",
    "ms": "Malay"
}

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState('English');

    useEffect(() => {
        setSelectedLanguage(Language_option[i18n.language as keyof typeof Language_option])
    }, [i18n?.language])

    const loginUser = async (username: string, password: string) => {
        try {
            const response = await axiosInstance.post("/auth/login", {
                username,
                password,
            })
            if (response?.data?.token) {
                setAuthToken(response?.data?.token);
                navigate('/account');
            } else {
                if (response?.data?.customize_message) {
                    Swal.fire({
                        html: `<div style='font-size:16px;text-align:left;'>${response?.data?.customize_message.replace(/\n/g, "<br/>")}</div>`,
                        icon: response?.data?.customize_icon ?? "info",
                    })
                } else {
                    Swal.fire({
                        title: 'Error!',
                        text: 'Invalid credentials',
                        icon: 'error',
                    })
                }
            }

        } catch (error: any) {
            console.error('Error fetching data:', error);
            if (error?.response?.status === 401) {
                navigate('/login');  // Redirect to login if unauthorized
            }
        }
    };

    const onFinish = async (values: any) => {
        console.log('Received values of form: ', values);
        let username = (values.username || '').trim();
        if (!username.startsWith('0')) {
            username = '0' + username;
        }
        await loginUser(username, values.password);
    };

    useEffect(() => {
        removeAuthToken();
    }, [])

    return (
        <AppShell backdrop="/images/bonia/bonia-front-store.webp" chrome={false}>
            <div className="lg:grid lg:grid-cols-[55fr_45fr] lg:min-h-[100dvh]">
                {/* Desktop-only imagery column */}
                <div
                    className="hidden lg:block bg-center bg-cover"
                    style={{ backgroundImage: "url('/images/bonia/BONIA_Store_front.webp')" }}
                    aria-hidden="true"
                />
                {/* Form column — existing markup, unchanged */}
                <div className="lg:flex lg:flex-col lg:justify-center lg:px-20 lg:bg-brand-50">
                    <img src='/images/bonia/gif-banner.webp' className='w-full lg:hidden' alt="" />
                    <div className='w-full mt-1'>
                        <div className='flex justify-between'>
                            <div className="text-2xl font-bold"></div>
                            <div className='w-[150px] font-bold mr-[15px]'>
                                <CustomSelect value={selectedLanguage} options={["English", "中文", "Malay"]}
                                    onChange={(e) => {
                                        const entry = Object.entries(Language_option).find(([_, val]) => val === e);
                                        i18n.changeLanguage(entry?.[0] ?? "en")
                                    }}
                                />
                            </div>
                        </div>

                        <img src="/images/bonia/Bonia-Logo-01.png" className='w-[25%] m-auto' alt="Bonia" />
                        <div className='text-2xl font-bold text-center mt-[30px] font-display lg:text-display-md'>{t('login_now')}</div>
                        <div className='text-sm font-bold text-center mt-[15px] mb-[15px]'>{t('please_login_to_continue')}</div>

                        <Form
                            name="login"
                            initialValues={{ remember: true }}
                            className='w-[300px] m-auto'
                            onFinish={onFinish}
                        >
                            <Form.Item
                                name="username"
                                rules={[{ required: true, message: '' }]}
                            >
                                <div className='flex bg-white rounded-md border-2 border-solid text-[16px] items-center'>
                                    <p className='px-[11px] py-[4px] min-w-fit'>{t("phoneNo")}</p>
                                    <Input className='text-right' size="large" placeholder={t("type_phoneNo")} variant="borderless" />
                                </div>
                            </Form.Item>
                            <Form.Item
                                name="password"
                                rules={[{ required: true, message: '' }]}
                            >
                                <div className='flex bg-white rounded-md border-2 border-solid text-[16px]'>
                                    <p className='px-[11px] py-[4px] min-w-fit'>{t("password")}</p>
                                    <Input className='text-right' type="password" size="large" placeholder={t("type_password")} variant="borderless" />
                                </div>
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    className='w-full text-white bg-brand-800'
                                    size='large' type="primary" htmlType="submit">
                                    {t("login_now")}
                                </Button>
                            </Form.Item>
                        </Form>

                        <div className='m-auto w-[80%] mt-[15px] flex justify-between font-bold text-brand-800'>
                            <div>{t("forget_password")}?</div>
                            <div onClick={() => navigate("/register")}>{t("register_now")}</div>
                        </div>


                        <div className='text-xs text-center mt-[45px] mb-[30px]'>Copyright © 2025 Bonia. All Rights Reserved.</div>

                    </div>
                </div>
            </div>
        </AppShell>
    );
};

export default LoginPage;
